import React, { useCallback, useEffect, useMemo, useState } from 'react';
import request from '../utils/request';

const ORDER_STATUS_COLORS = {
  ordered: 'bg-emerald-100 text-emerald-700',
  not_ordered: 'bg-amber-100 text-amber-700',
};

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const normalize = (value) => String(value || '').trim().toLowerCase();

const parseTime = (value) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const formatDate = (value) => {
  const time = parseTime(value);
  return time ? dateFormatter.format(new Date(time)) : '-';
};

const formatDateTime = (value) => {
  const time = parseTime(value);
  return time ? dateTimeFormatter.format(new Date(time)) : '-';
};

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);

const getHostName = (cart) => cart.host?.fullName || [cart.host?.firstName, cart.host?.lastName].filter(Boolean).join(' ') || '';

const getPropertyAddress = (cart) => cart.property?.addressLabel || cart.property?.listingTitle || cart.property?.uniqueId || '';

const getSortValue = (cart, field) => {
  switch (field) {
    case 'guestEmail':
      return normalize(cart.guestEmail);
    case 'hostName':
      return normalize(getHostName(cart));
    case 'propertyAddress':
      return normalize(getPropertyAddress(cart));
    case 'cartTotal':
      return Number(cart.cartTotal) || 0;
    case 'itemCount':
      return Number(cart.itemCount) || 0;
    case 'purchased':
      return cart.purchased ? 1 : 0;
    case 'lastVisited':
      return parseTime(cart.visits?.lastVisited);
    case 'inStockDate':
    case 'loadDateTime':
    default:
      return parseTime(cart[field]);
  }
};

const SavedCartsScreen = () => {
  const [savedCarts, setSavedCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderFilter, setOrderFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortField, setSortField] = useState('loadDateTime');
  const [sortDir, setSortDir] = useState('desc');

  const fetchSavedCarts = useCallback(async () => {
    try {
      setError(null);
      const { data } = await request.get('/admin/saved-carts');
      setSavedCarts(data.savedCarts || []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to load saved carts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSavedCarts();
  }, [fetchSavedCarts]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortArrow = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'desc' ? ' v' : ' ^';
  };

  const filtered = useMemo(() => {
    const now = Date.now();
    const query = normalize(searchTerm);

    return savedCarts.filter((cart) => {
      if (orderFilter === 'ordered' && !cart.purchased) return false;
      if (orderFilter === 'not_ordered' && cart.purchased) return false;

      const arrivalTime = parseTime(cart.inStockDate);
      if (dateFilter === 'upcoming' && (!arrivalTime || arrivalTime < now)) return false;
      if (dateFilter === 'past' && (!arrivalTime || arrivalTime >= now)) return false;
      if (dateFilter === 'missing_date' && arrivalTime) return false;

      if (!query) return true;
      const searchable = [
        cart.cartCode,
        cart.guestName,
        cart.guestEmail,
        getHostName(cart),
        cart.host?.email,
        cart.host?.companyName,
        getPropertyAddress(cart),
        cart.property?.listingTitle,
        cart.property?.uniqueId,
        cart.promoCode,
      ].map(normalize).join(' ');

      return searchable.includes(query);
    });
  }, [savedCarts, searchTerm, orderFilter, dateFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);

      if (typeof aVal === 'string' || typeof bVal === 'string') {
        const comparison = String(aVal).localeCompare(String(bVal));
        return sortDir === 'desc' ? -comparison : comparison;
      }

      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [filtered, sortField, sortDir]);

  const metrics = useMemo(() => {
    const orderedCount = savedCarts.filter((cart) => cart.purchased).length;
    const openCount = savedCarts.length - orderedCount;
    const filteredTotal = filtered.reduce((sum, cart) => sum + (Number(cart.cartTotal) || 0), 0);

    return {
      total: savedCarts.length,
      ordered: orderedCount,
      open: openCount,
      filteredTotal,
    };
  }, [savedCarts, filtered]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-zinc-50 p-6">
      <div className="max-w-[1500px] mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Saved Carts</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {metrics.total} total cart{metrics.total !== 1 ? 's' : ''} - {metrics.open} not ordered - {metrics.ordered} ordered
            </p>
          </div>
          <button
            onClick={() => { setLoading(true); fetchSavedCarts(); }}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Visible value</p>
            <p className="text-xl font-bold text-zinc-900 mt-1">{formatCurrency(metrics.filteredTotal)}</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Visible carts</p>
            <p className="text-xl font-bold text-zinc-900 mt-1">{filtered.length}</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Not ordered</p>
            <p className="text-xl font-bold text-amber-700 mt-1">{metrics.open}</p>
          </div>
          <div className="bg-white border border-zinc-200 rounded-lg p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Ordered</p>
            <p className="text-xl font-bold text-emerald-700 mt-1">{metrics.ordered}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm mb-4 p-4">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_180px_180px] gap-3">
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search guest, email, host, property, cart code"
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
            />
            <select
              value={orderFilter}
              onChange={(event) => setOrderFilter(event.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
            >
              <option value="all">All order states</option>
              <option value="not_ordered">Not ordered</option>
              <option value="ordered">Ordered</option>
            </select>
            <select
              value={dateFilter}
              onChange={(event) => setDateFilter(event.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400"
            >
              <option value="all">All arrival dates</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
              <option value="missing_date">Missing date</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('loadDateTime')}
                  >
                    Cart{sortArrow('loadDateTime')}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('guestEmail')}
                  >
                    Guest{sortArrow('guestEmail')}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('hostName')}
                  >
                    Host{sortArrow('hostName')}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors min-w-[260px]"
                    onClick={() => toggleSort('propertyAddress')}
                  >
                    Property Address{sortArrow('propertyAddress')}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('inStockDate')}
                  >
                    Arrival{sortArrow('inStockDate')}
                  </th>
                  <th
                    className="text-right px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('cartTotal')}
                  >
                    Cart Total{sortArrow('cartTotal')}
                  </th>
                  <th
                    className="text-right px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('itemCount')}
                  >
                    Items{sortArrow('itemCount')}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('lastVisited')}
                  >
                    Last Visit{sortArrow('lastVisited')}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('purchased')}
                  >
                    Ordered{sortArrow('purchased')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {sorted.map((cart) => {
                  const hostName = getHostName(cart);
                  const address = getPropertyAddress(cart);
                  const orderKey = cart.purchased ? 'ordered' : 'not_ordered';

                  return (
                    <tr key={cart._id || cart.cartCode} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-semibold text-zinc-900">{cart.cartCode || '-'}</div>
                        <div className="text-xs text-zinc-400">{formatDate(cart.loadDateTime)}</div>
                        {cart.link && (
                          <a
                            href={cart.link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
                          >
                            Open link
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-zinc-900">{cart.guestName || '-'}</div>
                        <div className="text-xs text-zinc-500">{cart.guestEmail || '-'}</div>
                      </td>
                      <td className="px-4 py-3 text-zinc-700">
                        <div className="font-medium">{hostName || 'Unknown host'}</div>
                        <div className="text-xs text-zinc-400">{cart.host?.companyName || cart.host?.email || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-zinc-700 min-w-[260px]">
                        <div className="font-medium text-zinc-900">{address || '-'}</div>
                        <div className="text-xs text-zinc-400">{cart.property?.listingTitle || cart.property?.uniqueId || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">
                        {formatDateTime(cart.inStockDate)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-zinc-900 whitespace-nowrap">
                        {formatCurrency(cart.cartTotal)}
                      </td>
                      <td className="px-4 py-3 text-right text-zinc-600 whitespace-nowrap">
                        {cart.itemCount || 0}
                        <span className="text-zinc-400 text-xs ml-1">({cart.uniqueItemCount || 0} unique)</span>
                      </td>
                      <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">
                        <div>{formatDateTime(cart.visits?.lastVisited)}</div>
                        <div className="text-xs text-zinc-400">{cart.visits?.count || 0} visit{cart.visits?.count === 1 ? '' : 's'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${ORDER_STATUS_COLORS[orderKey]}`}>
                          {cart.purchased ? 'Ordered' : 'Not ordered'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-zinc-400">
                      No saved carts found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavedCartsScreen;
